import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StreamingTranscriber } from 'assemblyai';

type TranscriptionState = {
  isRecording: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  connectionStatus: string;
  debugMessages: string[];
  audioLevel: number;
};

const PCM_SAMPLE_RATE = 16000;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const PUBLIC_AAI_API_KEY = import.meta.env.VITE_ASSEMBLYAI_API_KEY;

const addToLog = (
  setState: React.Dispatch<React.SetStateAction<TranscriptionState>>,
  message: string
) => {
  // Keep a lightweight debug log
  setState((prev) => ({
    ...prev,
    debugMessages: [
      ...prev.debugMessages,
      `${new Date().toISOString().substring(11, 19)}: ${message}`
    ]
  }));
};

const int16ToBase64 = (pcm: Int16Array): string => {
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

type Props = {
  onStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: (finalTranscript: string) => void;
  onInterim?: (text: string) => void;
  onFinalSegment?: (data: {
    text: string;
    words: {
      start: number;
      end: number;
      text: string;
      confidence: number;
      final: boolean;
    }[];
    endOfTurn: boolean;
  }) => void;
  onAudioLevel?: (level: number) => void;
  existingStream?: MediaStream;
  onStreamReady?: (stream: MediaStream) => void;
  hidden?: boolean;
};

export type AssemblyAITranscribeHandle = {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  pause: () => void;
  resume: () => void;
};

const AssemblyAITranscribe = React.forwardRef<
  AssemblyAITranscribeHandle,
  Props
>(
  (
    {
      onStart,
      onPause,
      onResume,
      onStop,
      onInterim,
      onFinalSegment,
      onAudioLevel,
      existingStream,
      onStreamReady,
      hidden
    },
    ref
  ) => {
    const [state, setState] = useState<TranscriptionState>({
      isRecording: false,
      transcript: '',
      interimTranscript: '',
      error: null,
      connectionStatus: 'Disconnected',
      debugMessages: [],
      audioLevel: 0
    });

    const transcriberRef = useRef<StreamingTranscriber | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const pausedRef = useRef<boolean>(false);

    const setupAudio = (stream: MediaStream) => {
      try {
        addToLog(setState, 'Initializing audio pipeline');
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)({ sampleRate: PCM_SAMPLE_RATE });
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyserRef.current = analyser;

        // @ts-ignore ScriptProcessorNode deprecated but broadly supported
        const processor: ScriptProcessorNode =
          audioContext.createScriptProcessor(4096, 1, 1);

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        analyser.connect(processor);
        processor.connect(audioContext.destination);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        processor.onaudioprocess = (e: AudioProcessingEvent) => {
          const input = e.inputBuffer.getChannelData(0);

          // Level meter
          if (analyserRef.current) {
            analyserRef.current.getByteFrequencyData(dataArray);
            const avg =
              dataArray.reduce((a, b) => a + b, 0) / dataArray.length || 0;
            const level = Math.min(100, Math.round((avg / 255) * 100));
            if (Math.abs(level - state.audioLevel) > 3) {
              setState((prev) => ({ ...prev, audioLevel: level }));
            }
            try {
              onAudioLevel && onAudioLevel(level);
            } catch {}
          }

          // Send PCM to AssemblyAI via SDK
          if (transcriberRef.current && !pausedRef.current) {
            const pcm = new Int16Array(input.length);
            for (let i = 0; i < input.length; i++) {
              const s = Math.max(-1, Math.min(1, input[i]));
              pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            try {
              // AssemblyAI expects ArrayBufferLike
              transcriberRef.current.sendAudio(pcm.buffer);
            } catch {}
          }
        };
        addToLog(setState, 'Audio pipeline ready');
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: 'Failed to initialize audio',
          connectionStatus: 'Error'
        }));
      }
    };

    const start = async () => {
      console.info('[Transcription] AssemblyAI start requested');
      setState((prev) => ({
        ...prev,
        isRecording: true,
        error: null,
        transcript: '',
        interimTranscript: '',
        connectionStatus: 'Connecting',
        debugMessages: []
      }));

      try {
        // 1) Fetch ephemeral token from our backend or use public dev key
        let token: string | undefined;
        let apiKey: string | undefined;
        if (
          PUBLIC_AAI_API_KEY &&
          String(PUBLIC_AAI_API_KEY).trim().length > 0
        ) {
          apiKey = String(PUBLIC_AAI_API_KEY).trim();
          addToLog(
            setState,
            'Using VITE_ASSEMBLYAI_API_KEY for streaming (dev)'
          );
        } else {
          addToLog(setState, 'Requesting AssemblyAI ephemeral token');
          console.info(
            '[Transcription] Requesting AssemblyAI token from backend'
          );
          const tokenResp = await fetch(
            `${API_BASE_URL}/assemblyai/token?force_curl=1`
          );
          if (!tokenResp.ok) {
            throw new Error(`Token request failed: ${tokenResp.status}`);
          }
          const tokenData = await tokenResp.json();
          token = tokenData?.token as string | undefined;
          if (!token) throw new Error('Missing token in response');
          addToLog(setState, 'Received ephemeral token');
        }

        // 2) Get microphone (reuse existingStream if provided)
        let stream: MediaStream | null = null;
        if (existingStream) {
          stream = existingStream;
        } else {
          addToLog(setState, 'Requesting microphone access');
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });
          addToLog(setState, 'Microphone access granted');
          try {
            onStreamReady && onStreamReady(stream);
          } catch {}
        }
        streamRef.current = stream;
        if (stream) setupAudio(stream);

        // 3) Initialize SDK transcriber and connect
        addToLog(setState, 'Initializing AssemblyAI SDK transcriber');
        const usedSampleRate = Math.round(
          (audioContextRef.current &&
            (audioContextRef.current as any).sampleRate) ||
            PCM_SAMPLE_RATE
        );
        addToLog(
          setState,
          `Using sample rate ${usedSampleRate} Hz (encoding pcm_s16le)`
        );
        const transcriber = new StreamingTranscriber({
          token,
          apiKey,
          sampleRate: usedSampleRate,
          encoding: 'pcm_s16le'
        });
        transcriberRef.current = transcriber;

        transcriber.on('open', () => {
          setState((prev) => ({ ...prev, connectionStatus: 'Connected' }));
          addToLog(setState, 'Transcriber connected');
          onStart && onStart();
        });

        transcriber.on('turn', (turn) => {
          const text = (turn as any)?.transcript as string | undefined;
          const words = (turn as any)?.words as
            | {
                start: number;
                end: number;
                confidence: number;
                text: string;
                word_is_final: boolean;
              }[]
            | undefined;
          const endOfTurn = (turn as any)?.end_of_turn as boolean | undefined;
          const interimFromWords = Array.isArray(words)
            ? words.map((w) => w.text).join(' ')
            : text || '';

          if (endOfTurn === false) {
            setState((prev) => ({
              ...prev,
              interimTranscript: interimFromWords
            }));
            onInterim && onInterim(interimFromWords);
            return;
          }

          if (text && (endOfTurn === true || endOfTurn === undefined)) {
            setState((prev) => ({
              ...prev,
              transcript: prev.transcript + (prev.transcript ? ' ' : '') + text,
              interimTranscript: ''
            }));
            onInterim && onInterim('');
            try {
              onFinalSegment &&
                onFinalSegment({
                  text,
                  words: Array.isArray(words)
                    ? words.map((w) => ({
                        start: w.start,
                        end: w.end,
                        text: w.text,
                        confidence: w.confidence,
                        final: !!w.word_is_final
                      }))
                    : [],
                  endOfTurn: true
                });
            } catch {}
          }
        });

        transcriber.on('error', (e) => {
          setState((prev) => ({
            ...prev,
            error: 'Transcription error',
            connectionStatus: 'Error'
          }));
          addToLog(setState, `SDK error: ${String(e)}`);
        });

        transcriber.on('close', (code: number, reason: string) => {
          setState((prev) => ({ ...prev, connectionStatus: 'Disconnected' }));
          addToLog(
            setState,
            `Transcriber closed (${code}): ${reason || 'no reason provided'}`
          );
        });

        addToLog(setState, 'Connecting to AssemblyAI streaming service');
        await transcriber.connect();
      } catch (err: any) {
        console.error('[Transcription] AssemblyAI start error:', err);
        setState((prev) => ({
          ...prev,
          isRecording: false,
          error: err?.message || 'Failed to start transcription',
          connectionStatus: 'Error'
        }));
        try {
          addToLog(setState, `Start error: ${err?.message || String(err)}`);
        } catch {}
      }
    };

    const stop = async () => {
      try {
        addToLog(setState, 'Stopping transcription');
        if (transcriberRef.current) {
          try {
            await transcriberRef.current.close();
          } catch {}
          transcriberRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      } finally {
        setState((prev) => ({ ...prev, isRecording: false }));
        onStop && onStop(state.transcript);
      }
    };

    const pause = () => {
      pausedRef.current = true;
      try {
        onPause && onPause();
      } catch {}
    };

    const resume = () => {
      pausedRef.current = false;
      try {
        onResume && onResume();
      } catch {}
    };

    useImperativeHandle(ref, () => ({ start, stop, pause, resume }), []);

    useEffect(() => {
      return () => {
        // Cleanup on unmount
        try {
          if (transcriberRef.current) {
            const maybePromise = transcriberRef.current.close?.();
            if (maybePromise && typeof maybePromise.then === 'function') {
              // best-effort, don't await in cleanup
              maybePromise.catch(() => {});
            }
            transcriberRef.current = null;
          }
        } catch {}
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };
    }, []);

    return (
      <div
        className="assemblyai-transcription-container"
        style={{ padding: 16, display: hidden ? 'none' : undefined }}
      >
        <h2>AssemblyAI Realtime Transcription (Minimal)</h2>

        {/* Connection status */}
        <div style={{ marginBottom: 8 }}>
          <strong>Connection:</strong> {state.connectionStatus}
        </div>

        {/* Audio level meter */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12
          }}
        >
          <div
            style={{
              width: 160,
              height: 10,
              background: '#eee',
              borderRadius: 4,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${state.audioLevel}%`,
                height: '100%',
                background:
                  state.audioLevel < 5
                    ? '#d32f2f'
                    : state.audioLevel < 20
                    ? '#f57c00'
                    : state.audioLevel < 50
                    ? '#fbc02d'
                    : '#388e3c'
              }}
            />
          </div>
          <span style={{ minWidth: 32 }}>{state.audioLevel}%</span>
        </div>

        {/* Controls */}
        <div style={{ marginBottom: 12 }}>
          {!state.isRecording ? (
            <button onClick={start}>Start</button>
          ) : (
            <button onClick={stop}>Stop</button>
          )}
        </div>

        {/* Error */}
        {state.error && (
          <div style={{ color: '#d32f2f', marginBottom: 12 }}>
            <strong>Error:</strong> {state.error}
          </div>
        )}

        {/* Transcript */}
        <div>
          <h3>Transcript</h3>
          <div
            style={{
              minHeight: 80,
              padding: 8,
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#fafafa'
            }}
          >
            {state.transcript}
            {state.interimTranscript && (
              <span style={{ opacity: 0.6, marginLeft: 4 }}>
                {state.interimTranscript}
              </span>
            )}
          </div>
        </div>

        {/* Debug Log */}
        <div style={{ marginTop: 16 }}>
          <h3>Debug Log</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button
              onClick={async () => {
                try {
                  const text = state.debugMessages.join('\n');
                  await navigator.clipboard.writeText(text);
                  addToLog(setState, 'Debug log copied to clipboard');
                } catch (e) {
                  addToLog(setState, 'Failed to copy debug log');
                }
              }}
            >
              Copy
            </button>
            <button
              onClick={() =>
                setState((prev) => ({ ...prev, debugMessages: [] }))
              }
            >
              Clear
            </button>
          </div>
          <div
            style={{
              height: 200,
              overflowY: 'auto',
              padding: 8,
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#0d1117',
              color: '#c9d1d9',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              whiteSpace: 'pre-wrap'
            }}
          >
            {state.debugMessages.length === 0 ? (
              <em>No logs yet</em>
            ) : (
              state.debugMessages.map((m, i) => <div key={i}>{m}</div>)
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default AssemblyAITranscribe;
